/**
 * Broadphase over the level primitives.
 *
 * The course is a 250 m vertical climb, so almost everything is separated in Y.
 * A flat array of AABBs bucketed into 8 m Y bands turns "what could I hit this
 * substep?" into one or two small bucket scans, which is all this game needs —
 * no BVH, no rebuilds.
 *
 * Primitives that *move* are the one exception, and they are handled by not
 * being in the grid at all: a mover's band would change every substep, and
 * rebuilding buckets at 120 Hz to spare a handful of overlap tests is a bad
 * trade. They live in a small flat list keyed on their **swept** bounds — the
 * conservative box their whole cycle stays inside, which never changes — and
 * every query scans it whole. The narrow phase is what makes it exact.
 */

import { Vector3 } from 'three';
import type { Primitive, V3 } from '@shared/primitives';
import type { MoverBounds, MoverSystem } from './moverSystem';

/** Height of one Y bucket, in metres. */
export const BAND_HEIGHT = 8;

interface Aabb {
  minX: number;
  minY: number;
  minZ: number;
  maxX: number;
  maxY: number;
  maxZ: number;
}

function bandOf(y: number): number {
  return Math.floor(y / BAND_HEIGHT);
}

/** World-space AABB of a primitive. Exported so level tooling can reuse it. */
export function primAabb(prim: Primitive): { min: Vector3; max: Vector3 } {
  const min = new Vector3();
  const max = new Vector3();
  writeAabb(prim, min, max);
  return { min, max };
}

function writeAabb(prim: Primitive, min: Vector3, max: Vector3): void {
  switch (prim.kind) {
    case 'box': {
      // Rotate the half-extents by the absolute value of the rotation matrix.
      const [qx, qy, qz, qw] = prim.quat;
      const xx = qx * qx;
      const yy = qy * qy;
      const zz = qz * qz;
      const xy = qx * qy;
      const xz = qx * qz;
      const yz = qy * qz;
      const wx = qw * qx;
      const wy = qw * qy;
      const wz = qw * qz;
      // Columns of the rotation matrix (local axes in world space).
      const m = [
        1 - 2 * (yy + zz), 2 * (xy - wz), 2 * (xz + wy),
        2 * (xy + wz), 1 - 2 * (xx + zz), 2 * (yz - wx),
        2 * (xz - wy), 2 * (yz + wx), 1 - 2 * (xx + yy),
      ];
      const hx = prim.half[0];
      const hy = prim.half[1];
      const hz = prim.half[2];
      const ex = Math.abs(m[0]!) * hx + Math.abs(m[1]!) * hy + Math.abs(m[2]!) * hz;
      const ey = Math.abs(m[3]!) * hx + Math.abs(m[4]!) * hy + Math.abs(m[5]!) * hz;
      const ez = Math.abs(m[6]!) * hx + Math.abs(m[7]!) * hy + Math.abs(m[8]!) * hz;
      min.set(prim.center[0] - ex, prim.center[1] - ey, prim.center[2] - ez);
      max.set(prim.center[0] + ex, prim.center[1] + ey, prim.center[2] + ez);
      break;
    }
    case 'sphere': {
      const r = prim.radius;
      min.set(prim.center[0] - r, prim.center[1] - r, prim.center[2] - r);
      max.set(prim.center[0] + r, prim.center[1] + r, prim.center[2] + r);
      break;
    }
    case 'cylinder':
    case 'capsule': {
      // Conservative for a cylinder (the true bound shrinks on the cap axes),
      // exact for a capsule. Both are cheap and tight enough for bucketing.
      const r = prim.radius;
      const a: V3 = prim.a;
      const b: V3 = prim.b;
      min.set(Math.min(a[0], b[0]) - r, Math.min(a[1], b[1]) - r, Math.min(a[2], b[2]) - r);
      max.set(Math.max(a[0], b[0]) + r, Math.max(a[1], b[1]) + r, Math.max(a[2], b[2]) + r);
      break;
    }
  }
}

const _min = new Vector3();
const _max = new Vector3();

/**
 * Spatial index over a level's primitives.
 *
 * Build once at level load. Queries return freshly allocated, de-duplicated
 * arrays — the caller owns the result and may hold onto it.
 */
export class Broadphase {
  private readonly prims: readonly Primitive[];
  private readonly boxes: Aabb[] = [];
  /** Y band index → indices into `prims`. Static primitives only. */
  private readonly bands = new Map<number, number[]>();
  /** Indices into `prims` of the movers, whose boxes are their swept bounds. */
  private readonly dynamic: number[] = [];
  private readonly movers: MoverSystem | null;
  /** Per-primitive query stamp, so a primitive spanning many bands is emitted once. */
  private readonly stamp: Int32Array;
  private queryId = 0;
  private minBand = 0;
  private maxBand = 0;

  /**
   * @param movers when supplied, its primitives are indexed by their swept
   *   bounds and filtered on solidity instead of being bucketed. Omitting it
   *   leaves every primitive in the static grid at its rest pose — which is what
   *   a headless consumer that never ticks a clock wants.
   */
  constructor(prims: Primitive[], movers?: MoverSystem) {
    this.prims = prims;
    this.movers = movers ?? null;
    this.stamp = new Int32Array(prims.length);
    this.stamp.fill(-1);

    const swept = new Map<Primitive, MoverBounds>();
    if (movers !== undefined) for (const b of movers.dynamicBounds()) swept.set(b.prim, b);

    let lo = Infinity;
    let hi = -Infinity;
    for (let i = 0; i < prims.length; i++) {
      const prim = prims[i]!;
      const bounds = swept.get(prim);
      if (bounds !== undefined) {
        this.boxes.push({
          minX: bounds.min[0], minY: bounds.min[1], minZ: bounds.min[2],
          maxX: bounds.max[0], maxY: bounds.max[1], maxZ: bounds.max[2],
        });
        this.dynamic.push(i);
        continue;
      }
      writeAabb(prim, _min, _max);
      this.boxes.push({
        minX: _min.x, minY: _min.y, minZ: _min.z,
        maxX: _max.x, maxY: _max.y, maxZ: _max.z,
      });
      const b0 = bandOf(_min.y);
      const b1 = bandOf(_max.y);
      if (b0 < lo) lo = b0;
      if (b1 > hi) hi = b1;
      for (let b = b0; b <= b1; b++) {
        let bucket = this.bands.get(b);
        if (bucket === undefined) {
          bucket = [];
          this.bands.set(b, bucket);
        }
        bucket.push(i);
      }
    }
    this.minBand = Number.isFinite(lo) ? lo : 0;
    this.maxBand = Number.isFinite(hi) ? hi : 0;
  }

  /** Number of indexed primitives. */
  get size(): number {
    return this.prims.length;
  }

  /** Every primitive whose AABB overlaps the world-space box `[min, max]`. */
  queryAABB(min: Vector3, max: Vector3): Primitive[] {
    const out: Primitive[] = [];
    const id = ++this.queryId;
    // Clamping keeps a query far above the summit (or below the plaza) from
    // walking thousands of empty bands.
    const b0 = Math.max(this.minBand, bandOf(min.y));
    const b1 = Math.min(this.maxBand, bandOf(max.y));
    for (let b = b0; b <= b1; b++) {
      const bucket = this.bands.get(b);
      if (bucket === undefined) continue;
      for (let k = 0; k < bucket.length; k++) {
        const i = bucket[k]!;
        if (this.stamp[i] === id) continue;
        this.stamp[i] = id;
        const a = this.boxes[i]!;
        if (a.maxX < min.x || a.minX > max.x) continue;
        if (a.maxY < min.y || a.minY > max.y) continue;
        if (a.maxZ < min.z || a.minZ > max.z) continue;
        out.push(this.prims[i]!);
      }
    }
    // The movers, tested against the box their whole cycle stays inside. A
    // `blink` platform in its off window is simply not there this substep, and
    // handing it back would let the narrow phase land a body on nothing.
    const dyn = this.dynamic;
    for (let k = 0; k < dyn.length; k++) {
      const i = dyn[k]!;
      if (this.stamp[i] === id) continue;
      this.stamp[i] = id;
      const a = this.boxes[i]!;
      if (a.maxX < min.x || a.minX > max.x) continue;
      if (a.maxY < min.y || a.minY > max.y) continue;
      if (a.maxZ < min.z || a.minZ > max.z) continue;
      const prim = this.prims[i]!;
      if (this.movers !== null && !this.movers.isActive(prim)) continue;
      out.push(prim);
    }
    return out;
  }

  /**
   * Every primitive whose AABB overlaps the segment `from → to` grown by `pad`.
   * `pad` should be the swept sphere's radius (plus any safety margin).
   */
  querySegment(from: Vector3, to: Vector3, pad: number): Primitive[] {
    _min.set(
      Math.min(from.x, to.x) - pad,
      Math.min(from.y, to.y) - pad,
      Math.min(from.z, to.z) - pad,
    );
    _max.set(
      Math.max(from.x, to.x) + pad,
      Math.max(from.y, to.y) + pad,
      Math.max(from.z, to.z) + pad,
    );
    return this.queryAABB(_min, _max);
  }
}
